import React, { CSSProperties, useCallback, useState } from 'react';
import { TCoreImageOptions } from '../types/options';
import { ColorSvgImage } from './ColorSvgImage';
import styles from './image.module.css';

type ImageProps = {
    src: string;
    dataTestId?: string;
    imgStyles?: Record<string, string>;
    alt?: string;
    backgroundColor?: string;
    label?: string;
    spinnerDelay?: number;
    defaultSrc?: string;
    defaultIcon?: string;
    /** Icon identifiers (matched as substrings of `src`) that must keep their original colors instead of being recolored via the SVG mask. */
    iconsWithoutColorMask?: string[];
    onImgError?: () => void;
} & TCoreImageOptions;

// Whether `src` is excluded from color masking — matched as a substring against any list entry (filename/path fragment).
function isColorMaskExcluded(src?: string, iconsWithoutColorMask: string[] = []): boolean {
    if (!src) return false;
    return iconsWithoutColorMask.some((entry) => src.includes(entry));
}

function isColorSVG(imageUrl?: string, color?: string): boolean {
    if (!imageUrl) return false;
    const lower = imageUrl.toLowerCase();
    const isSvg = lower.endsWith('.svg') || lower.endsWith('.svg#symb');
    return isSvg && !!color;
}

const DEFAULT_SPINNER_DELAY_MS = 300;

const Image = ({
    src,
    borderRadius,
    iconColor,
    iconColorDisabled,
    isDisabled,
    label,
    backgroundColor,
    dataTestId = '',
    alt = '',
    imgStyles = {},
    width = 24,
    defaultIconWidth = 24,
    height = 24,
    defaultIconHeight = 24,
    spinnerDelay = DEFAULT_SPINNER_DELAY_MS,
    defaultSrc,
    defaultIcon,
    iconsWithoutColorMask,
    onImgError,
}: ImageProps) => {
    const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
    const [prevSrc, setPrevSrc] = useState(src);

    if (src !== prevSrc) {
        setPrevSrc(src);
        setStatus('loading');
    }

    // Detect images the browser already has ready (e.g. from cache) by inspecting the real,
    // lazily-loaded <img> element — without triggering an eager fetch that would defeat loading="lazy".
    // The element may already be `complete` before React attaches onLoad, so onLoad can never fire for it;
    // resolve the loading state directly from the element instead. We gate on `complete` alone (not
    // `naturalWidth > 0`) because a valid but zero-intrinsic-size SVG (only a viewBox, no width/height)
    // reports `naturalWidth === 0` yet is fully loaded — gating on width would leave it hidden forever.
    const handleImgRef = useCallback((img: HTMLImageElement | null) => {
        if (img?.complete) {
            setStatus('loaded');
        }
    }, []);

    const loading = status === 'loading';
    const imgLoadError = status === 'error';
    const spinnerSize = typeof width === 'number' && typeof height === 'number' ? Math.min(width, height, 24) : (width ?? height);

    if (isColorSVG(src, iconColor) && !isColorMaskExcluded(src, iconsWithoutColorMask)) {
        return (
            <ColorSvgImage
                src={src}
                iconColor={iconColor}
                iconColorDisabled={iconColorDisabled}
                isDisabled={isDisabled}
                width={width}
                height={height}
                borderRadius={borderRadius}
                dataTestId={dataTestId}
                spinnerDelay={spinnerDelay}
                spinnerSize={spinnerSize}
                defaultSrc={defaultIcon ?? defaultSrc}
            />
        );
    }

    if (src) {
        if (imgLoadError && defaultIcon) {
            return (
                <ColorSvgImage
                    src={defaultIcon}
                    iconColor={iconColor ?? ''}
                    iconColorDisabled={iconColorDisabled}
                    isDisabled={isDisabled}
                    width={defaultIconWidth ?? width}
                    height={defaultIconHeight ?? height}
                    borderRadius={borderRadius}
                    spinnerSize={spinnerSize}
                    dataTestId={dataTestId}
                    spinnerDelay={spinnerDelay}
                />
            );
        }

        return (
            <div
                style={{
                    position: 'relative',
                    width,
                    height,
                    borderRadius,
                    overflow: 'hidden',
                    backgroundColor,
                    flexShrink: 0,
                }}
            >
                {loading && (
                    <div
                        className={styles.spinnerContainer}
                        style={
                            {
                                '--spinner-delay': `${spinnerDelay}ms`,
                            } as CSSProperties
                        }
                    >
                        <div className={styles.spinner} style={{ width: spinnerSize, height: spinnerSize }} />
                    </div>
                )}
                <img
                    ref={handleImgRef}
                    alt={alt}
                    data-test={dataTestId}
                    src={src}
                    loading="lazy"
                    style={
                        {
                            width: '100%',
                            height: '100%',
                            display: 'block',
                            visibility: loading ? 'hidden' : 'visible',
                            ...imgStyles,
                        } as CSSProperties
                    }
                    onLoad={() => setStatus('loaded')}
                    onError={(e) => {
                        setStatus('error');
                        if (defaultIcon) {
                            onImgError?.();
                        } else if (defaultSrc) {
                            const img = e.target as HTMLImageElement;
                            img.onerror = null;
                            img.src = defaultSrc;
                        }
                    }}
                />
            </div>
        );
    }

    if (!src && defaultIcon) {
        return (
            <ColorSvgImage
                src={defaultIcon}
                iconColor={iconColor ?? ''}
                iconColorDisabled={iconColorDisabled}
                isDisabled={isDisabled}
                width={defaultIconWidth ?? width}
                height={defaultIconHeight ?? height}
                borderRadius={borderRadius}
                spinnerSize={spinnerSize}
                dataTestId={dataTestId}
                spinnerDelay={spinnerDelay}
            />
        );
    }

    if (label) {
        return (
            <div
                data-test={dataTestId}
                style={
                    {
                        width,
                        height,
                        borderRadius,
                        backgroundColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: (isDisabled ? iconColorDisabled : iconColor) ?? 'inherit',
                        // fontSize: Math.round(Math.min(width, height) * 0.4),
                        fontWeight: 500,
                        userSelect: 'none',
                        flexShrink: 0,
                    } as CSSProperties
                }
            >
                {label}
            </div>
        );
    }

    return null;
};

export { Image };
